import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import toast from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "../../../../common/hooks/hooks";
import { openGlobalModal } from "../../../../common/features/globalModal/globalModalSlice";
import { useMutationWithFeedback } from "../../../../common/hooks/useMutationWithFeedback";
import { useProcessCheckoutMutation } from "../api/checkoutApi";
import { selectCustomerNote } from "../checkoutSelectors";
import type { ProcessCheckoutPayload } from "../../../../types/api/wcStoreApi/wcStoreApiCheckoutEndpointsTypes";
import type { TransformedAddress } from "../../cart/types/cartApiTypes";
import type {
  TransformedCheckoutData,
  TransformedPaymentResult,
} from "../types/checkoutApiTypes";

// Helper to map TransformedAddress back to API Raw format (snake_case)
const mapAddressToApiFormat = (address: TransformedAddress | undefined) => {
  if (!address) return {} as any;
  return {
    first_name: address.firstName,
    last_name: address.lastName,
    company: address.company,
    address_1: address.address1,
    address_2: address.address2,
    city: address.city,
    state: address.state,
    postcode: address.postcode,
    country: address.country || "", // Don't force TR
    email: address.email,
    phone: address.phone,
  };
};

/**
 * Handles payment method specific logic for headless flows (e.g. Iyzico/PayTR Modal).
 * Returns `true` if the payment was handled (e.g. modal opened), preventing default redirect.
 */
const handleHeadlessPayment = (
  paymentMethodId: string,
  paymentResult: TransformedPaymentResult,
  dispatch: any,
  t: TFunction,
): boolean => {
  const gatewayIds = window.commonVariables?.paymentGatewayIds;

  if (!gatewayIds) return false;

  // --- IYZICO ---
  if (paymentMethodId === gatewayIds.IYZICO) {
    const iyzicoHtmlB64 =
      paymentResult?.paymentDetails?.iyzico_html_content_b64;

    if (iyzicoHtmlB64) {
      try {
        const iyzicoHtml = atob(iyzicoHtmlB64);
        dispatch(
          openGlobalModal({
            contentKey: "paymentForm",
            title: t("checkout.paymentModal.title", "Secure Payment"),
            contentProps: {
              iyzicoHtmlContent: iyzicoHtml, // Updated Prop Name
              containerId: "iyzipay-checkout-form",
            },
          }),
        );
        return true; // Handled successfully (Modal opened)
      } catch (e) {
        console.error("Failed to decode Iyzico content", e);
        // Return false to allow fallback to redirect
      }
    }
  }

  // --- PAYTR ---
  if (paymentMethodId === gatewayIds.PAYTR) {
    const paytrToken = paymentResult?.paymentDetails?.paytr_token;

    if (paytrToken) {
      dispatch(
        openGlobalModal({
          contentKey: "paymentForm",
          title: t("checkout.paymentModal.title", "Secure Payment"),
          contentProps: {
            paytrToken: paytrToken, // Pass token directly
            containerId: "paytr-checkout-form",
          },
        }),
      );
      return true; // Handled successfully
    }
  }

  return false; // Not handled, proceed to redirect
};

/**
 * A composite hook that orchestrates the entire order submission process.
 */
export const useCheckoutSubmit = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // 1. Get the current customer note from Redux state
  const customerNote = useAppSelector(selectCustomerNote);

  const [processCheckout, mutationResult] = useProcessCheckoutMutation();

  const { trigger: triggerPlaceOrder, isLoading: isProcessing } =
    useMutationWithFeedback<any, ProcessCheckoutPayload>({
      mutation: [processCheckout, mutationResult],
      loadingMessage: t(
        "feedback.checkout.processing",
        "Processing your order...",
      ),
      // CHANGE: Disable automatic success toast. We will show it manually if needed.
      successMessage: null,
      errorMessages: {
        default: t(
          "feedback.checkout.error",
          "There was an error processing your order.",
        ),
      },
    });

  const submitOrder = useCallback(
    async (
      checkoutData: TransformedCheckoutData,
      selectedPaymentMethod: string,
    ) => {
      // 1. Prepare Addresses
      const billingAddr = mapAddressToApiFormat(checkoutData.billingAddress);
      const shippingAddr = mapAddressToApiFormat(checkoutData.shippingAddress);

      // 2. Prepare Extensions
      const extensions = {
        additional_billing_info: {
          customer_type: checkoutData.billingAddress?.customerType,
          tckn: checkoutData.billingAddress?.tckn,
          vkn: checkoutData.billingAddress?.vkn,
        },
        legal_agreements: {
          agreed: true,
        },
      };

      // 3. Trigger Mutation
      const result = await triggerPlaceOrder({
        payment_method: selectedPaymentMethod,
        billing_address: billingAddr,
        shipping_address: shippingAddr,
        customer_note: customerNote, // Standard key for POST
        extensions: extensions,
      });

      // 4. Handle Success
      if (result.success && result.data) {
        const { paymentResult, orderId } = result.data; // orderId is available here

        // --- GATEWAY INITIALIZATION ERROR CHECK ---
        // Check if the backend reported a "soft" failure via payment details
        // (e.g. Invalid API Key, Gateway Down), even though HTTP status was 200.
        if (paymentResult?.paymentDetails?.gateway_init_error) {
          const errorMessage =
            paymentResult.paymentDetails.messages ||
            t(
              "feedback.checkout.gatewayInitError",
              "Payment method unavailable. Please try another.",
            );
          toast.error(errorMessage, { duration: 5000 });
          // Stop execution here to prevent redirecting or clearing session
          return;
        }

        // A. Save billing email for Order Page verification
        if (checkoutData.billingAddress?.email) {
          sessionStorage.setItem(
            "wc_customer_billing_email",
            checkoutData.billingAddress.email,
          );
        }

        // B. Save Order ID to session storage
        // The OrderPage will check this ID to clear the cart.
        if (orderId) {
          sessionStorage.setItem("latest_order_id", String(orderId));
        }

        // --- HEADLESS PAYMENT HANDLER ---
        const handled = handleHeadlessPayment(
          selectedPaymentMethod,
          paymentResult,
          dispatch,
          t,
        );

        if (handled) {
          // If handled (modal open), stop here.
          // Note: Cart clearing happens on the "Order Received" page redirect after Iyzico/PayTR callback success.
          return;
        }

        // --- STANDARD REDIRECT LOGIC (BACS, COD, etc.) ---
        toast.success(
          t("feedback.checkout.success", "Order placed successfully!"),
        );

        if (paymentResult?.redirectUrl) {
          window.location.href = paymentResult.redirectUrl;
        }
      }
    },
    [triggerPlaceOrder, dispatch, t, customerNote],
  );

  return { submitOrder, isProcessing };
};
